// src/components/home/ConsultancySection.jsx
import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, CheckCircle2, CarFront, Users, Handshake, BadgeCheck } from "lucide-react";
import SectionTitle from "../common/SectionTitle";
import Button from "../common/Button";

const steps = [
  {
    n: "01",
    icon: CarFront,
    title: "Share Your Vehicle",
    description: "Give us your vehicle details and selling requirements.",
  },
  {
    n: "02",
    icon: Users,
    title: "We Find Buyers",
    description: "We present your vehicle to interested customers.",
  },
  {
    n: "03",
    icon: Handshake,
    title: "We Handle the Process",
    description: "Our team helps coordinate enquiries and communication.",
  },
  {
    n: "04",
    icon: BadgeCheck,
    title: "Complete Your Sale",
    description: "You deal directly with the buyer and complete the sale.",
  },
];

const ConsultancySection = ({ onSubmitVehicle }) => {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const element = sectionRef.current;

    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(element);
        }
      },
      {
        threshold: 0.12,
      }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden bg-[#F8FAF9] py-16 sm:py-20 lg:py-24"
    >
      {/* Decorative background */}
      <div className="pointer-events-none absolute -right-32 top-10 h-72 w-72 rounded-full bg-[#F97316]/10 blur-3xl" />
      <div className="pointer-events-none absolute -left-32 bottom-0 h-72 w-72 rounded-full bg-[#00A260]/10 blur-3xl" />

      <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">

        {/* Heading */}
        <div
          className={`transform transition-all duration-700 ease-out ${
            visible
              ? "translate-y-0 opacity-100"
              : "translate-y-8 opacity-0"
          }`}
        >
          <SectionTitle
            eyebrow="NJV MOTORS"
            title="Want to Sell Your Vehicle?"
            subtitle="Give your vehicle selling card to NJV Motors and let us help you connect with potential buyers."
            align="center"
          />
        </div>

        {/* Intro */}
        <div
          className={`mx-auto mt-5 max-w-2xl text-center transform transition-all delay-100 duration-700 ease-out ${
            visible
              ? "translate-y-0 opacity-100"
              : "translate-y-8 opacity-0"
          }`}
        >
          <p className="text-sm leading-6 text-[#475569] sm:text-base sm:leading-7">
            Whether you are selling a car, van, SUV or other vehicle,
            our team can help showcase your vehicle to customers looking
            for genuine vehicles.
          </p>
        </div>

        {/* Steps */}
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:mt-14 lg:grid-cols-4">
          {steps.map((step, index) => {
            const Icon = step.icon;

            return (
              <div
                key={step.n}
                className={`group relative transform transition-all duration-700 ease-out ${
                  visible
                    ? "translate-y-0 opacity-100"
                    : "translate-y-10 opacity-0"
                }`}
                style={{
                  transitionDelay: `${150 + index * 100}ms`,
                }}
              >
                <div className="relative h-full overflow-hidden rounded-3xl border border-[#E2E8E5] bg-white p-5 shadow-sm transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-xl sm:p-6">

                  {/* Step number */}
                  <div className="absolute right-5 top-5 text-4xl font-black text-[#F97316]/10">
                    {step.n}
                  </div>

                  {/* Icon */}
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#F97316]/10 text-[#F97316] transition-all duration-300 group-hover:scale-110 group-hover:bg-[#F97316] group-hover:text-white">
                    <Icon className="h-5 w-5" />
                  </div>

                  {/* Content */}
                  <div className="mt-6">
                    <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#F97316]">
                      Step {step.n}
                    </p>

                    <h3 className="mt-2 text-base font-bold leading-6 text-[#061A3A] sm:text-lg">
                      {step.title}
                    </h3>

                    <p className="mt-2 text-sm leading-6 text-[#64748B]">
                      {step.description}
                    </p>
                  </div>

                  {/* Bottom accent */}
                  <div className="absolute bottom-0 left-0 h-1 w-0 bg-[#F97316] transition-all duration-300 group-hover:w-full" />
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA Card */}
        <div
          className={`mt-10 transform transition-all delay-500 duration-700 ease-out sm:mt-14 ${
            visible
              ? "translate-y-0 opacity-100"
              : "translate-y-8 opacity-0"
          }`}
        >
          <div className="relative overflow-hidden rounded-3xl bg-[#061A3A] px-6 py-8 shadow-xl sm:px-8 sm:py-10 lg:px-10">

            {/* Decorative circles */}
            <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full border border-white/10" />
            <div className="pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full border border-white/10" />

            <div className="relative flex flex-col gap-7 lg:flex-row lg:items-center lg:justify-between">

              {/* Text */}
              <div className="max-w-xl">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-[#F97316]" />

                  <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#F97316]">
                    NJV Motors Consultancy
                  </span>
                </div>

                <h3 className="mt-3 text-2xl font-extrabold leading-tight text-white sm:text-3xl">
                  Ready to sell your vehicle?
                </h3>

                <p className="mt-3 text-sm leading-6 text-white/70 sm:text-base">
                  Submit your vehicle details and our team will get in touch
                  with you regarding your selling requirement.
                </p>
              </div>

              {/* CTA */}
              <div className="shrink-0">
                <Button
                  variant="orange"
                  size="lg"
                  onClick={onSubmitVehicle}
                  className="w-full shadow-lg shadow-orange-500/20 sm:w-auto"
                >
                  Submit Your Vehicle
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Trust text */}
        <div
          className={`mt-6 text-center transform transition-all delay-700 duration-700 ${
            visible
              ? "translate-y-0 opacity-100"
              : "translate-y-5 opacity-0"
          }`}
        >
          <p className="text-xs text-[#64748B] sm:text-sm">
            Vehicle selling assistance from{" "}
            <span className="font-semibold text-[#061A3A]">
              NJV Motors
            </span>
          </p>
        </div>
      </div>
    </section>
  );
};

export default ConsultancySection;